/**
 * ideaImpactView
 *
 * Pure view-model derivations for the idea Impact panel. No React, no
 * fetching: the backend ships the rolled-up totals, these helpers only
 * format and shape them for display, so the math stays unit-testable.
 */

import type { ImpactBlock, ImpactTriple } from "./services/ideaImpact";

/**
 * Organic share of a metric as a 0–1 fraction, or null when the total
 * is missing / zero (no meaningful split to draw).
 */
export function organicShare(triple: ImpactTriple): number | null {
  const total = triple.total;
  if (!total || total <= 0) return null;
  const organic = triple.organic ?? total;
  return Math.max(0, Math.min(1, organic / total));
}

/** Compact count formatting: 1234 -> "1.2K", 2_500_000 -> "2.5M". */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${trim(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${trim(value / 1_000)}K`;
  return `${Math.round(value)}`;
}

/** Currency formatting for paid spend. Euro is the platform default. */
export function formatSpend(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `€${value.toFixed(2)}`;
}

/** Percentage label for an organic share fraction (e.g. 0.73 -> "73%"). */
export function formatShare(fraction: number | null): string {
  if (fraction === null) return "—";
  return `${Math.round(fraction * 100)}%`;
}

export interface ImpactMetricRow {
  key: "reach" | "impressions" | "engagement";
  label: string;
  triple: ImpactTriple;
  /** Organic share fraction (null when not splittable). */
  organicShare: number | null;
  /** True when a paid leg was measured (drives whether we show the split). */
  hasPaidSplit: boolean;
}

const METRIC_LABELS: Record<ImpactMetricRow["key"], string> = {
  reach: "Reach",
  impressions: "Impressions",
  engagement: "Engagement",
};

/**
 * Shape one impact block (platform or combined) into the rows the panel
 * renders. Engagement has no paid leg, so its split is suppressed.
 */
export function buildMetricRows(block: ImpactBlock): ImpactMetricRow[] {
  return (["reach", "impressions", "engagement"] as const).map((key) => {
    const triple = block[key];
    return {
      key,
      label: METRIC_LABELS[key],
      triple,
      organicShare: organicShare(triple),
      hasPaidSplit: triple.paid !== null && triple.paid !== undefined,
    };
  });
}

function trim(n: number): string {
  // One decimal, but drop a trailing ".0" (1.0K -> 1K).
  return n.toFixed(1).replace(/\.0$/, "");
}
